/* DASHBOARD - Dynamic Flow Implementation */
import React, { useMemo, useState } from 'react';
import { useStore } from '../store/StoreContext';
import { calculateSchedule, getPriorityColor, getStatusColor, normalizeStatus, toLocalISO } from '../utils/helpers';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

export const Dashboard = () => {
  const { state } = useStore();
  const [viewDate, setViewDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const formatDisplayDate = (date) => {
    const todayStr = toLocalISO(new Date());
    const targetStr = toLocalISO(date);
    if (todayStr === targetStr) return "Today's Active Tasks";
    
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (toLocalISO(tomorrow) === targetStr) return "Tomorrow's Schedule";
    
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (toLocalISO(yesterday) === targetStr) return "Yesterday's Recap";
    
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  const adjustDate = (days) => {
    const next = new Date(viewDate);
    next.setDate(next.getDate() + days);
    setViewDate(next);
  };

  const resetToToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setViewDate(d);
  };

  const stats = useMemo(() => {
    const projects = Object.values(state.projects || {}).filter(p => p.status !== 'archived');
    let totalTasks = 0;
    let completedTasks = 0;

    projects.forEach(p => {
      (p.workflows || []).forEach(wf => {
        (wf.tasks || []).forEach(t => {
          totalTasks++;
          if (normalizeStatus(t.status) === 'completed') completedTasks++;
        });
      });
    });

    return {
      totalProjects: projects.length,
      activeProjects: projects.filter(p => p.status === 'active').length,
      completionRate: totalTasks ? Math.round((completedTasks / totalTasks) * 100) : 0,
      totalTasks
    };
  }, [state.projects]);

  const filteredTasks = useMemo(() => {
    const targetStr = toLocalISO(viewDate);
    const isToday = targetStr === toLocalISO(new Date());
    let list = [];

    Object.values(state.projects || {})
      .filter(p => p.status !== 'archived')
      .forEach(p => {
        const scheduled = calculateSchedule(p);
        const allTasks = [];
        
        scheduled.scheduledWorkflows.forEach(wf => {
          wf.scheduledTasks.forEach(t => {
            allTasks.push({ 
              ...t, 
              projectName: p.name, 
              color: p.color, 
              businessUnitId: p.businessUnitId 
            });
          });
        });

        if (isToday) {
          // 1. Get all uncompleted tasks scheduled for today or earlier
          const activeTasks = allTasks.filter(t => 
            t.status !== 'completed' && t.startDate <= targetStr
          );

          if (activeTasks.length > 0) {
            list.push(...activeTasks);
          } else {
            // 2. FLOW LOGIC: If today is clear, show the very next uncompleted task
            const nextUp = allTasks.find(t => t.status !== 'completed');
            if (nextUp) list.push(nextUp);
          }
        } else {
          // For other days, show what's scheduled for that day (regardless of completion)
          const dayTasks = allTasks.filter(t => 
            targetStr >= t.startDate && targetStr <= t.endDate
          );
          list.push(...dayTasks);
        }
      });

    return list;
  }, [state.projects, viewDate]);

  const businessUnits = Object.values(state.businessUnits || {});

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-8 h-full overflow-y-auto bg-base-900 custom-scrollbar">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold">Executive Overview</h1>
          <p className="text-sm text-gray-500">Real-time portfolio performance</p>
        </div>
        <div className="text-xs text-gray-500 font-mono bg-base-800 px-3 py-1 rounded-full border border-base-700">
          Local: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[
          { label: 'Projects', value: stats.totalProjects, icon: 'Folder', color: 'text-blue-400' },
          { label: 'Active', value: stats.activeProjects, icon: 'Activity', color: 'text-accent' },
          { label: 'Total Tasks', value: stats.totalTasks, icon: 'CheckSquare', color: 'text-purple-400' },
          { label: 'Completion', value: `${stats.completionRate}%`, icon: 'TrendingUp', color: 'text-green-400' }
        ].map((stat, i) => (
          <div key={i} className="bg-base-800 rounded-xl p-6 border border-base-700 flex items-center gap-4 shadow-lg">
            <div className={`p-3 rounded-lg bg-base-900 ${stat.color}`}>
              <SafeIcon name={stat.icon} className="w-6 h-6" />
            </div>
            <div>
              <div className="text-gray-400 text-sm">{stat.label}</div>
              <div className="text-2xl font-bold">{stat.value}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-base-800 rounded-xl border border-base-700 p-6 flex flex-col min-h-[500px]">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary border border-primary/20">
                <SafeIcon name="Calendar" className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white tracking-tight">{formatDisplayDate(viewDate)}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] text-gray-500 uppercase tracking-widest font-black">
                    {toLocalISO(viewDate)}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-base-600"></span>
                  <span className="text-[10px] text-primary uppercase tracking-widest font-black">
                    {filteredTasks.length} {filteredTasks.length === 1 ? 'Task' : 'Tasks'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-1 bg-base-900 p-1.5 rounded-xl border border-base-700 shadow-inner">
              <button onClick={() => adjustDate(-1)} className="p-2.5 hover:bg-base-700 rounded-lg text-gray-400 hover:text-white transition-all active:scale-95" title="Previous Day">
                <SafeIcon name="ChevronLeft" className="w-5 h-5" />
              </button>
              <button onClick={resetToToday} className={`px-4 py-1.5 text-[11px] font-black uppercase tracking-widest rounded-lg transition-all border-x border-base-700 mx-1 ${toLocalISO(viewDate) === toLocalISO(new Date()) ? 'text-primary' : 'text-gray-500 hover:text-white'}`}>
                Today
              </button>
              <button onClick={() => adjustDate(1)} className="p-2.5 hover:bg-base-700 rounded-lg text-gray-400 hover:text-white transition-all active:scale-95" title="Next Day">
                <SafeIcon name="ChevronRight" className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="space-y-3 flex-1 overflow-y-auto custom-scrollbar max-h-[500px] pr-2">
            <AnimatePresence mode="wait">
              {filteredTasks.length === 0 ? (
                <motion.div key="empty" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="h-full flex flex-col items-center justify-center text-gray-500 py-20">
                  <div className="w-20 h-20 bg-base-900 rounded-3xl flex items-center justify-center mb-6 border border-base-700 shadow-inner">
                    <SafeIcon name="Coffee" className="w-10 h-10 opacity-10" />
                  </div>
                  <p className="text-sm font-medium text-gray-600">No tasks scheduled for this date.</p>
                </motion.div>
              ) : (
                <motion.div key={toLocalISO(viewDate)} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3 pb-4">
                  {filteredTasks.map((task, i) => {
                    const bu = state.businessUnits[task.businessUnitId];
                    return (
                      <motion.div key={`${task.id}-${i}`} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }} className="flex items-center gap-4 p-4 rounded-2xl bg-base-900 border border-base-700 hover:border-primary/40 transition-all group hover:shadow-lg hover:shadow-primary/5">
                        <div className="flex items-center gap-[6px] shrink-0">
                          <a href={bu?.website || '#'} target="_blank" rel="noopener noreferrer" className={`w-11 h-11 rounded-xl bg-base-800 border border-base-700 flex items-center justify-center overflow-hidden shadow-inner transition-all hover:scale-105 group-hover:border-base-600 ${!bu?.website && 'pointer-events-none'}`}>
                            {bu?.logoUrl ? (
                              <img src={bu.logoUrl} className="w-full h-full object-contain p-1.5" alt="" />
                            ) : (
                              <SafeIcon name="Briefcase" className="w-5 h-5 text-gray-600" />
                            )}
                          </a>
                          <a href={task.appUrl || '#'} target="_blank" rel="noopener noreferrer" className={`w-11 h-11 rounded-xl bg-base-800 border border-base-700 flex items-center justify-center overflow-hidden shadow-inner transition-all hover:scale-105 group-hover:border-base-600 ${!task.appUrl && 'pointer-events-none'}`}>
                            {task.logoUrl ? (
                              <img src={task.logoUrl} className="w-full h-full object-contain p-1.5" alt="" />
                            ) : (
                              <SafeIcon name="Zap" className="w-5 h-5 text-gray-600" />
                            )}
                          </a>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm truncate text-white group-hover:text-primary transition-colors">{task.name}</div>
                          <div className="text-[10px] text-gray-500 uppercase tracking-[0.1em] truncate font-black mt-0.5">{task.projectName}</div>
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <div className="flex items-center gap-2">
                            <div className={`text-[9px] px-2 py-0.5 rounded-md font-black uppercase tracking-tighter border ${getPriorityColor(task.priority)}`}>
                              {task.priority}
                            </div>
                            <div className="w-2.5 h-2.5 rounded-full shadow-inner border border-white/5" style={{ backgroundColor: getStatusColor(task.status) }}></div>
                          </div>
                          <div className="w-12 h-1 rounded-full opacity-40" style={{ backgroundColor: task.color }}></div>
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="bg-base-800 rounded-xl border border-base-700 p-6 flex flex-col max-h-[640px]">
          <h2 className="text-lg font-bold mb-6 flex items-center gap-3">
            <div className="p-2 bg-accent/10 rounded-lg text-accent border border-accent/20">
              <SafeIcon name="Briefcase" className="w-5 h-5" />
            </div>
            Business Units
          </h2>
          <div className="space-y-4 overflow-y-auto pr-2 custom-scrollbar flex-1">
            {businessUnits.length === 0 ? (
              <div className="py-12 text-center text-gray-600 italic text-sm">No units registered.</div>
            ) : (
              businessUnits.map(bu => (
                <div key={bu.id} className="p-4 rounded-2xl bg-base-900 border border-base-700 flex flex-col gap-4 group hover:border-base-600 transition-all hover:shadow-lg">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl bg-base-800 border border-base-700 flex items-center justify-center shrink-0 overflow-hidden shadow-inner`}>
                      {bu.logoUrl ? (
                        <img src={bu.logoUrl} alt={bu.name} className="w-full h-full object-contain p-1.5" />
                      ) : (
                        <span className="text-primary font-black text-xl">{bu.name.charAt(0)}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-sm truncate">{bu.name}</div>
                      <div className="text-[10px] text-gray-500 font-bold uppercase tracking-tighter mt-0.5">
                        {Object.values(state.projects).filter(p => p.businessUnitId === bu.id && p.status !== 'archived').length} Active Projects
                      </div>
                    </div>
                  </div>
                  {bu.website && (
                    <a href={bu.website} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary hover:text-primary-hover flex items-center gap-2 font-black uppercase tracking-widest transition-colors py-2 px-3 bg-primary/5 rounded-lg border border-primary/10 w-fit">
                      <SafeIcon name="ExternalLink" className="w-3.5 h-3.5" />
                      Launch Unit
                    </a>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};